<template>
	<div class="breakdown-card">
		<div class="breakdown-card__body">
			<div>
				<div
					v-for="row in rows"
					:key="row.label"
					class="breakdown-card__row"
					:class="{ 'breakdown-card__row_total': row.emphasis }"
				>
					<Text :font="row.emphasis ? '--text-14-20-semi' : '--text-14-20-med'" color="--text-dark-primary">
						{{ row.label }}
					</Text>
					<Text :font="row.emphasis ? '--text-14-20-semi' : '--text-14-20-med'" color="--text-dark-primary">
						{{ row.value }}
					</Text>
				</div>
			</div>
			<Text v-if="$slots.default" font="--text-14-20-med" color="--text-dark-primary">
				<slot />
			</Text>
		</div>
		<div v-if="keepMedication" class="breakdown-card__keep-wrap">
			<div class="breakdown-card__keep">
				<img :src="boxIcon" alt="" width="20" height="20" />
				<Text font="--text-14-20-semi" color="--text-dark-primary">You keep all the medication you’ve received</Text>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
// The money card on /cancel/commitment/end-now, /end-after and /refund (Figma 7415:18138, 7415:18411):
// line items 44 apart, the total, an explanation, and the "You keep all the medication" note.
import { Text } from '@simple-life-app/growth-ui-kova'
import boxIcon from '@/assets/icons/box.svg'
import { KOVA } from '@/designTokens'

export interface BreakdownRow {
	label: string
	value: string
	/** Semibold, centred on a wrapped label, no divider under it — the total line. */
	emphasis?: boolean
}

withDefaults(
	defineProps<{
		rows: BreakdownRow[]
		keepMedication?: boolean
	}>(),
	{ keepMedication: true },
)
</script>

<style scoped>
.breakdown-card {
	display: flex;
	flex-direction: column;
	width: 100%;
	background: var(--background-white);
	border-radius: var(--size-24);
}

.breakdown-card__body {
	display: flex;
	flex-direction: column;
	gap: var(--size-12);
	padding: var(--size-12) var(--size-20);
}

/* Figma draws the divider inside the row (`border/black`), so it takes no space: KOVA.divider. */
.breakdown-card__row {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: var(--size-16);
	padding: var(--size-12) 0;
	box-shadow: v-bind('KOVA.divider');
	font-variant-numeric: tabular-nums;
}

.breakdown-card__row > :first-child {
	flex: 1 1 0;
	min-width: 0;
}

.breakdown-card__row > :last-child {
	flex-shrink: 0;
	text-align: right;
	white-space: nowrap;
}

.breakdown-card__row_total {
	align-items: center;
	box-shadow: none;
}

.breakdown-card__keep-wrap {
	padding: var(--size-12);
}

/* `brand/shot/shot-quaternary` #d4f7c4 — no DS token. */
.breakdown-card__keep {
	display: flex;
	align-items: flex-start;
	gap: var(--size-8);
	padding: var(--size-16) var(--size-12);
	background: #d4f7c4;
	border-radius: var(--size-12);
}

.breakdown-card__keep img {
	flex-shrink: 0;
}
</style>
